import type { MonospaceWorkspace, ResolvedMonospaceServiceAccount } from '#layers/data-model/public';
import { useOverlay } from '@nuxt/ui/composables';
import ServiceAccountDialog from '#layers/access/components/ServiceAccounts/ServiceAccountDialog.vue';
import { useSystemEntityStore } from '#layers/runtime/public';

export function useServiceAccountDialogs() {
	const overlay = useOverlay();
	const store = useSystemEntityStore();
	const form = store.MonospaceServiceAccount.createForm({
		defaultValues: () => ({ fullName: '' }),
	});

	/**
	 * Opens the create dialog and resolves with the created account, or `undefined` when the
	 * dialog was dismissed. Callers need the account itself: it may sort outside the first page
	 * of any listing, so it cannot be recovered by re-querying and diffing.
	 */
	async function create(
		{ prefillWorkspaces }: { prefillWorkspaces?: MonospaceWorkspace[] } = {},
	): Promise<ResolvedMonospaceServiceAccount | undefined> {
		form.$reset();
		const modal = overlay.create(ServiceAccountDialog, {
			props: { form, isEdit: false, serviceAccount: null, prefillWorkspaces },
		});

		const created = await modal.open().result;
		return typeof created === 'object' && created !== null ? created : undefined;
	}

	async function edit(serviceAccount: ResolvedMonospaceServiceAccount) {
		const editForm = await store.MonospaceServiceAccount.updateForm(serviceAccount.id);
		const modal = overlay.create(ServiceAccountDialog, {
			props: { form: editForm, serviceAccount, isEdit: true },
		});

		await modal.open().result;
	}

	return { create, edit };
}
